import React from 'react'
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography, Chip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../../../../constants/system'

export interface SystemContextAccordionProps {
  customer: string
  job: string
}

/**
 * Collapsed, read-only "Powered by Bayes Price analysis framework" panel
 * (#970 Part A, Step 2). Non-prompters never need to open it; power users can.
 */
export const SystemContextAccordion: React.FC<SystemContextAccordionProps> = ({ customer, job }) => {
  const systemPromptsQuery = useQuery({
    queryKey: ['system-prompts', customer, job],
    enabled: Boolean(customer && job),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // Dummy multi-table ID — the endpoint only needs customer/job for merged config.
      const res = await axios.get(`${API_BASE_URL}/multitables/${customer}/${job}/_/prompt-stack`)
      return res.data as { assembled_system_prompt: string }
    },
  })

  const systemPrompts = systemPromptsQuery.data
  if (!systemPrompts) return null

  return (
    <Accordion disableGutters sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" fontWeight={600}>ⓘ Powered by Bayes Price analysis framework</Typography>
          <Chip label="read-only" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1, maxHeight: 240, overflow: 'auto' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}>
            {systemPrompts.assembled_system_prompt}
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default SystemContextAccordion
